import { entityDraftHasContent } from "@/components/alerts/add-v2/model/entityRules";
import {
  BulletinFormField,
  BulletinFormValues,
} from "@/components/alerts/add-v2/model/types";
import {
  BULLETIN_SECTION_IDS,
  BulletinSectionId,
} from "@/components/alerts/add-v2/sections/sectionIds";

import { BulletinSchema } from "./schemas";

/**
 * Turns the schema's issues into the list the page works from: ordered, grouped by section, and
 * countable.
 *
 * This is the file that replaces `layout/sectionCompletion.ts`, which approximated all of it from
 * phase 1 until there was a schema to ask.
 */

export interface BulletinIssue {
  section: BulletinSectionId;
  /** The react-hook-form path, e.g. `contacts.0.email`. */
  path: string;
  /** The top-level field it belongs to — what decides its section and its place in the order. */
  field: BulletinFormField;
  /** Where the schema reported it, which is the order to show two issues on the same field in. */
  order: number;
  /** Already translated: the schema holds the copy, so every reader shows the same sentence. */
  message: string;
  /** Set on entity issues, so a jump can open the card that is actually incomplete. */
  entityId?: string;
}

/**
 * Every field, in the order it appears on the page, with the section it belongs to.
 *
 * Two jobs in one table, which is why it is a list and not a map: `sectionOf` reads the pairing,
 * and the index is what orders issues within a section — so "the first issue" is the topmost one on
 * screen, which is where a jump has to land.
 */
const FIELD_SECTIONS: readonly [BulletinFormField, BulletinSectionId][] = [
  ["policeService", "setup"],
  ["bulletinType", "setup"],
  ["occurrenceNumbers", "setup"],
  ["operationalArea", "setup"],

  ["associatedCrimes", "incident"],
  ["locations", "incident"],
  ["incidentTimes", "incident"],

  ["entities", "entities"],

  ["media", "media"],
  ["pdf", "media"],

  ["description", "narrative"],
  ["descriptionFr", "narrative"],
  ["originalLanguage", "narrative"],

  ["classification", "contact"],
  ["contacts", "contact"],
  ["approvedBy", "contact"],
  ["approvedByBadge", "contact"],
  ["issuedTime", "contact"],
];

const fieldRank = (field: BulletinFormField): number =>
  FIELD_SECTIONS.findIndex(([name]) => name === field);

export const sectionOf = (
  field: BulletinFormField,
): BulletinSectionId | undefined =>
  FIELD_SECTIONS.find(([name]) => name === field)?.[1];

/**
 * Runs the schema and returns every issue it found, in document order.
 *
 * Deliberately a plain function over the values rather than a read of `formState.errors`:
 * react-hook-form only writes an error for the field that changed, so its error object answers
 * "what should this input show", not "what is still missing" — and the design's count is always
 * visible, on a form nobody has touched yet.
 */
export const collectIssues = (
  values: BulletinFormValues,
  schema: BulletinSchema,
): BulletinIssue[] => {
  const result = schema.safeParse(values);
  if (result.success) return [];

  return result.error.issues
    .flatMap<BulletinIssue>((issue, order) => {
      const field = issue.path[0] as BulletinFormField;
      const section = sectionOf(field);
      // Nothing today, but a schema key that is not in the table above belongs to no section and
      // so has nowhere to be shown or counted — dropping it beats putting it in an arbitrary one.
      if (!section) return [];

      const row = typeof issue.path[1] === "number" ? issue.path[1] : null;

      return [
        {
          section,
          order,
          path: issue.path.join("."),
          field,
          message: issue.message,
          ...(field === "entities" && row !== null
            ? { entityId: values.entities[row]?.id }
            : {}),
        },
      ];
    })
    .sort((a, b) => {
      const bySection =
        BULLETIN_SECTION_IDS.indexOf(a.section) -
        BULLETIN_SECTION_IDS.indexOf(b.section);
      if (bySection !== 0) return bySection;

      const byField = fieldRank(a.field) - fieldRank(b.field);
      // Within one field the schema's own order is already the right one: the rule modules report a
      // row's problems in the order its inputs appear, and a list reports its rows in order.
      // Sorting by path instead would put `dateOfBirth` above `involvement`.
      return byField !== 0 ? byField : a.order - b.order;
    });
};

export type SectionIssues = Record<BulletinSectionId, BulletinIssue[]>;

const emptySectionIssues = (): SectionIssues =>
  BULLETIN_SECTION_IDS.reduce((grouped, id) => {
    grouped[id] = [];
    return grouped;
  }, {} as SectionIssues);

export const groupBySection = (issues: BulletinIssue[]): SectionIssues =>
  issues.reduce((grouped, issue) => {
    grouped[issue.section].push(issue);
    return grouped;
  }, emptySectionIssues());

export type SectionCompletion = Record<BulletinSectionId, boolean>;

/**
 * The rail ticks.
 *
 * A required section is complete when nothing in it is outstanding. **Entities and media never
 * block Finalize**, so "nothing outstanding" would tick them on an untouched form — they show a
 * tick once they hold something instead, which is the rule `layout/sectionCompletion.ts` carried
 * and the only part of that file worth keeping.
 */
export const computeSectionCompletion = (
  values: BulletinFormValues,
  issues: BulletinIssue[],
): SectionCompletion => {
  const grouped = groupBySection(issues);
  const clean = (id: BulletinSectionId) => grouped[id].length === 0;

  return {
    setup: clean("setup"),
    incident: clean("incident"),
    // `entityDraftHasContent` rather than a count: clicking Add puts an empty row in the document,
    // and an empty row is not an entity yet.
    entities: clean("entities") && values.entities.some(entityDraftHasContent),
    media: values.media.length > 0,
    narrative: clean("narrative"),
    contact: clean("contact"),
  };
};
